import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as authApi from '../api/authApi';
import { SESSION_EXPIRED_EVENT } from '../api/http';
import { logoutOAuth } from '../api/oauthApi';
import {
  clearTokens,
  getAccessToken,
  getUserId,
  persistTokens,
  isOAuthSession,
  SESSION_UPDATED_EVENT,
} from './tokenStore';

interface AuthState {
  userId: string | null;
  accessToken: string | null;
  email: string | null;
  isInitialized: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<authApi.LoginResponse>;
  register: (input: authApi.RegisterInput) => Promise<authApi.RegistrationResponse>;
  logout: () => Promise<void>;
  acceptOAuthSession: (session: authApi.LoginResponse) => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [userId, setUserId] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  const applySession = useCallback(() => {
    setAccessToken(getAccessToken());
    setUserId(getUserId());
  }, []);

  useEffect(() => {
    // Restore any persisted session on mount.
    applySession();
    setIsInitialized(true);

    const onExpired = () => {
      clearTokens();
      setAccessToken(null);
      setUserId(null);
      setEmail(null);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    window.addEventListener(SESSION_UPDATED_EVENT, applySession);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
      window.removeEventListener(SESSION_UPDATED_EVENT, applySession);
    };
  }, [applySession]);

  const login = useCallback(
    async (emailArg: string, password: string, rememberMe = true) => {
      const res = await authApi.login(emailArg, password, rememberMe);
      applySession();
      setEmail(res.requiresMfa ? null : emailArg);
      return res;
    },
    [applySession],
  );

  const register = useCallback((input: authApi.RegisterInput) => authApi.register(input), []);

  const logout = useCallback(async () => {
    const token = getAccessToken();
    const oauth = isOAuthSession() || Boolean(logoutError);
    clearTokens();
    setAccessToken(null);
    setUserId(null);
    setEmail(null);
    const cookieLogout = logoutOAuth().then(() => setLogoutError(null)).catch(() => {
      if (oauth) setLogoutError('Local session cleared, but the service could not clear its sign-in cookie. Reconnect and retry sign out, or clear this site’s cookies.');
    });
    await Promise.all([cookieLogout, token ? authApi.logout(token) : Promise.resolve()]);
  }, [logoutError]);

  const acceptOAuthSession = useCallback((session: authApi.LoginResponse) => {
    if (session.requiresMfa) {
      clearTokens();
      applySession();
      setEmail(null);
      return;
    }
    if (!session.accessToken || session.userId == null) throw new Error('The sign-in service returned an incomplete session. Please sign in again.');
    persistTokens({ ...session, accessToken: session.accessToken, sessionMode: 'oauth', rememberMe: true });
    applySession();
    setEmail(null);
  }, [applySession]);

  const value = useMemo<AuthState>(
    () => ({
      userId,
      accessToken,
      email,
      isInitialized,
      isAuthenticated: Boolean(accessToken && userId),
      login,
      register,
      logout,
      acceptOAuthSession,
    }),
    [userId, accessToken, email, isInitialized, login, register, logout, acceptOAuthSession],
  );

  return <AuthContext.Provider value={value}>
    {logoutError && <div role="alert">{logoutError}{' '}<button onClick={logout}>Retry sign out</button></div>}
    {children}
  </AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
